import './index.less';
import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    getFramework,
    getLayerId,
    useHoverId,
    useIdentity,
    useIsMouseSelect,
    useScale,
} from '@/framework';
import { getLocaleStore } from '@/i18n';
import { getLayerInfoWithRotate } from '@/utils/layer-kit';
import { IExportPlugin, LayerType, PluginType } from '@/version-types';
import { getGroupPickState } from '../states/group-inner-state';
import { MouseHover } from './hover';

const Hover: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const { $t } = getLocaleStore();
    const tipNodeRef = useRef<HTMLDivElement>(null);
    const tipTxtMap: Record<string, string> = {
        [LayerType.TEXT_LAYER]: $t((v) => v.stage.defaultText),
        [LayerType.IMAGE_LAYER]: $t((v) => v.stage.viewImageInfo),
    };
    const [isTempLayer, setIsTempLayer] = useState(false);
    const hoverId = useHoverId();
    const scale = useScale();
    const isMouseSelect = useIsMouseSelect();
    const hoverTargetRef = useRef<any>(null);

    const mouseHoverPlugin = framework.getInstance(MouseHover);
    const [doubleClickTipVisible, setDoubleClickTipVisible] = useState(mouseHoverPlugin.isDoubleClickTipVisible());

    useEffect(() => {
        return mouseHoverPlugin.onVisibleChange((visible) => {
            setDoubleClickTipVisible(visible);
        });
    }, []);

    useEffect(() => {
        const handle = framework.events.onMouseMove((ev) => {
            if (framework.operate.isDisablePick() || framework.operate.isDisableHover()) return;
            if (getGroupPickState(identity).getIsActive()) return;
            const target = ev.target as HTMLElement;
            const layerId = getLayerId(identity, target);
            if (layerId) {
                const activeLayer = framework.operate.getActiveLayer();
                if (activeLayer?.id === layerId) {
                    hoverTargetRef.current = null;
                    framework.operate.delHoverId();
                    return;
                }
                const currentHoverId = framework.operate.getHoverId();
                if (currentHoverId !== hoverId) {
                    const layerOriginIndex = framework.tryGetLayerOriginIndex(layerId);
                    if (
                        framework.operate.isMouseSelect() &&
                        isNumber(layerOriginIndex) &&
                        layerOriginIndex !== framework.stage.getActiveOriginIndex()
                    ) {
                        hoverTargetRef.current = null;
                        framework.operate.delHoverId();
                        return;
                    }
                }
            }
            hoverTargetRef.current = layerId ? target : null;
            framework.operate.setHoverId(layerId || '');
        });
        return () => {
            framework.events.off(handle);
        };
    }, []);

    useEffect(() => {
        if (hoverId) {
            const layer = framework.getLayer(hoverId);

            framework.operate.hoverExtend.hover(hoverTargetRef.current, layer?.layerType);
            if (layer.__isTempLayer) {
                setIsTempLayer(true);
            } else {
                setIsTempLayer(false);
            }
            const info = getLayerInfoWithRotate(layer);
            if (tipNodeRef.current) {
                tipNodeRef.current.style.left = `${info.left * scale}px`;
                tipNodeRef.current.style.top = `${info.top * scale - 40}px`;
            }
        } else framework.operate.hoverExtend.clearCursor();
    }, [hoverId, scale]);

    if (!hoverId || framework.operate.isDisableMouseSelect() || isTempLayer) return null;

    const layer = framework.getLayer(hoverId);
    if (!layer) return null;

    const tipText = tipTxtMap[layer.layerType] || '';
    const tipStyle = {
        display: layer.lock || !tipText ? 'none' : 'flex',
    };

    return (
        <>
            {isMouseSelect || !doubleClickTipVisible ? null : (
                <BaseStagePlugin originIndexOrLayerId={hoverId} style={{ zIndex: 20 }} name="hover-layer-tip">
                    <div ref={tipNodeRef} style={{ ...tipStyle }} className="c-editor-tip">
                        {tipText}
                    </div>
                </BaseStagePlugin>
            )}
            <BaseStagePlugin originIndexOrLayerId={hoverId} name="hover-layer">
                <BaseOperateView layer={layer}>
                    <div
                        className={classNames('hover-operate', {
                            'is-group': framework.checkIsGroup(layer),
                            'is-lock': layer.lock,
                        })}
                    ></div>
                </BaseOperateView>
            </BaseStagePlugin>
        </>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 1,
    name: 'hover-layer',
    render() {
        return <Hover />;
    },
    createInstance({ identity }) {
        return [{ key: 'MouseHover', value: new MouseHover(identity) }];
    },
};

export default exportPlugin;
